import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import z from 'zod'
import { AudioPrompt } from '../components/audio-prompt'
import { v2validations } from '../config/validation.config'
import type { SidebarComponentProps, VoicemailNodeData } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = VoicemailNodeData['config']

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).voicemail
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        type: 'text',
      },
    },
    {
      name: 'prompt',
      label: 'Prompt',
      type: 'custom',
      component: (props) => (
        <AudioPrompt
          {...(props as CompProps<FormValues, FormValues['prompt']>)}
          nodeVars={nodeVars}
        />
      ),
      zodValidation: validations.shape.prompt,
      eventDataExtractor: (data) => data as Record<string, unknown>,
    },
    {
      name: 'play_beep',
      zodValidation: validations.shape.play_beep,
      label: 'Play beep after prompt',
      componentType: 'checkbox',
    },
    {
      name: 'prompt.delay',
      zodValidation: z.number({ message: 'Required' }),
      label: 'Wait before prompt',
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
      },
    },
    {
      name: 'transcription_type',
      zodValidation: validations.shape.transcription_type,
      label: 'Transcription Type',
      componentType: 'select',
      inputProps: {
        options: [
          { label: 'None', value: 'none' },
          { label: 'Auto', value: 'auto' },
          { label: 'Hybrid', value: 'hybrid' },
        ],
        searchable: false,
      },
      afterOnChange: (values, _, form) => {
        if (values.transcription_type === 'none') {
          form?.setFieldValue('transcription_url', '')
        }
      },
    },
    {
      name: 'transcription_url',
      zodValidation: validations.shape.transcription_url,
      label: 'Transcription URL',
      componentType: 'input',
      inputProps: {
        inputMode: 'url',
      },
      onChangeListenTo: ['transcription_type'],
      visible: (values) => values.transcription_type !== 'none',
    },
    {
      name: 'voicemail_timeout',
      zodValidation: validations.shape.voicemail_timeout,
      label: 'End Recording on Silence',
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
      },
    },
    {
      name: 'voicemail_length',
      zodValidation: validations.shape.voicemail_length,
      label: 'Voicemail Length',
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
      },
    },
    {
      name: 'end_recording',
      label: 'End recording on digit ',
      zodValidation: validations.shape.end_recording,
      componentType: 'select',
      inputProps: {
        searchable: false,
        options: [
          { label: '*', value: '*' },
          { label: '1', value: '1' },
          { label: '2', value: '2' },
          { label: '3', value: '3' },
          { label: '4', value: '4' },
          { label: '5', value: '5' },
          { label: '6', value: '6' },
          { label: '7', value: '7' },
          { label: '8', value: '8' },
          { label: '9', value: '9' },
          { label: '#', value: '#' },
        ],
      },
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const VoicemailSidebarComponent = (props: SidebarComponentProps<VoicemailNodeData>) => {
  const { nodeVars } = props
  const formFields = fields(nodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
